import { useState, useEffect } from "react";
import { TemplateSelector } from "./TemplateSelector";
import { TemplateParamsEditor } from "./TemplateParamsEditor";
import { brevoTemplateService } from "../../services/brevoTemplateService";

interface EmailEditorProps {
  templateId: string;
  onTemplateIdChange: (templateId: string) => void;
  templateParams: Record<string, any>;
  onTemplateParamsChange: (params: Record<string, any>) => void;
  subject?: string;
  preheader?: string;
  subjectEditable?: boolean;
  onSubjectChange?: (subject: string) => void;
  onPreheaderChange?: (preheader: string) => void;
}

export function EmailEditor({
  templateId,
  onTemplateIdChange,
  templateParams,
  onTemplateParamsChange,
  subject,
  preheader,
  subjectEditable = true,
  onSubjectChange,
  onPreheaderChange,
}: EmailEditorProps) {
  const [templateSubject, setTemplateSubject] = useState("");
  const [templatePreheader, setTemplatePreheader] = useState("");

  useEffect(() => {
    const loadTemplateDefaults = async () => {
      if (!templateId) {
        setTemplateSubject("");
        setTemplatePreheader("");
        return;
      }
      try {
        const response = await brevoTemplateService.getTemplateById(templateId);
        setTemplateSubject(response.data?.subject || "");
        setTemplatePreheader(response.data?.preheader || "");
      } catch (error) {
        console.error("Failed to load template defaults:", error);
      }
    };
    loadTemplateDefaults();
  }, [templateId]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#102040]">
            Email Content
          </span>
        </div>
      </div>

      <TemplateSelector value={templateId} onChange={onTemplateIdChange} />

      {templateId && (
        <>
          {subjectEditable && onSubjectChange && (
            <div>
              <label className="block text-sm font-medium text-[#102040] mb-2">
                Email Subject {templateSubject && "(Optional Override)"}
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => onSubjectChange(e.target.value)}
                placeholder={templateSubject || "Enter subject line..."}
                className="flex h-[44px] w-full rounded-[7px] border border-[#dbdbdb] bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e07828] focus-visible:ring-offset-2"
              />
              {templateSubject && (
                <p className="text-xs text-[#989797] mt-1">
                  Template default: {templateSubject}
                </p>
              )}
            </div>
          )}

          {onPreheaderChange && (
            <div>
              <label className="block text-sm font-medium text-[#102040] mb-2">
                Preheader Text {templatePreheader && "(Optional Override)"}
              </label>
              <input
                type="text"
                value={preheader}
                onChange={(e) => onPreheaderChange(e.target.value)}
                placeholder={
                  templatePreheader || "Optional preview text in inbox..."
                }
                className="flex h-[44px] w-full rounded-[7px] border border-[#dbdbdb] bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e07828] focus-visible:ring-offset-2"
              />
              {templatePreheader && (
                <p className="text-xs text-[#989797] mt-1">
                  Template default: {templatePreheader}
                </p>
              )}
            </div>
          )}

          <TemplateParamsEditor
            templateId={templateId}
            value={templateParams}
            onChange={onTemplateParamsChange}
          />
        </>
      )}
    </div>
  );
}
